import { useMemo, useState } from "react";
import { getPlan } from "../../shared/plans";
import { IconAlert, IconBraces, IconBuilding, IconCheck, IconCopy, IconReload, IconSearch, IconTeiaMark, IconWeb } from "./Icons";
import {
  MarkApi,
  MarkClaude,
  MarkCline,
  MarkCopilot,
  MarkCursor,
  MarkDeepSeek,
  MarkGemini,
  MarkGrok,
  MarkGroup,
  MarkMistral,
  MarkOllama,
  MarkOpenAI,
  MarkPerplexity,
  MarkQwen,
  MarkTerminal,
  MarkVsCode,
  MarkWindsurf,
} from "./AiMarks";

/** Endereço real do site em produção; em dev cai no host aberto no navegador. */
function siteOrigin(): string {
  if (typeof window === "undefined") return "https://teia.holdnexus.com";
  const { origin } = window.location;
  return origin.includes("localhost") || origin.includes("127.0.0.1")
    ? "https://teia.holdnexus.com"
    : origin;
}

function CopyBlock({ label, code }: { label?: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code-block">
      {label && <span className="code-label">{label}</span>}
      <pre><code>{code}</code></pre>
      <button
        className="btn btn-secondary btn-sm code-copy"
        onClick={() => {
          navigator.clipboard?.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }).catch(() => setCopied(false));
        }}
      >
        {copied ? <><IconCheck size={14} /> Copiado</> : <><IconCopy size={14} /> Copiar</>}
      </button>
    </div>
  );
}

type Client = {
  id: string;
  name: string;
  mark: JSX.Element;
  kind: "remoto" | "ponte" | "api";
  summary: string;
  steps: string[];
  code?: { label?: string; code: string };
  note?: string;
};

function buildClients(origin: string, bridge: string): Client[] {
  const remote = `${origin}/mcp`;
  return [
    {
      id: "claude",
      name: "Claude",
      mark: <MarkClaude size={38} />,
      kind: "remoto",
      summary: "Claude.ai, Claude Desktop e Claude Code falam MCP remoto direto.",
      steps: [
        "No Claude.ai ou no Desktop: Configurações → Conectores → Adicionar conector personalizado.",
        `Cole a URL ${remote} e salve. Não há login nem chave: o Teia é aberto.`,
        "No Claude Code, o comando abaixo resolve em uma linha.",
      ],
      code: { label: "Terminal", code: `claude mcp add --transport http teia ${remote}` },
    },
    {
      id: "chatgpt",
      name: "ChatGPT",
      mark: <MarkOpenAI size={38} />,
      kind: "remoto",
      summary: "Conectores personalizados no modo desenvolvedor (planos pagos, na web).",
      steps: [
        "Abra o ChatGPT na web: Configurações → Apps → Configurações avançadas.",
        "Ligue o modo desenvolvedor.",
        `Volte em Apps → Criar, escolha servidor MCP e informe ${remote}, sem autenticação.`,
        "Na conversa, ative o Teia nas ferramentas e pergunte sobre qualquer CNPJ.",
      ],
      note: "O modo desenvolvedor exige Plus, Pro, Business, Enterprise ou Edu, e só aparece na versão web.",
    },
    {
      id: "codex",
      name: "Codex CLI",
      mark: <MarkGroup size={38} marks={[MarkOpenAI, MarkTerminal]} />,
      kind: "remoto",
      summary: "MCP por HTTP direto, com o cliente RMCP ligado.",
      steps: ["Edite ~/.codex/config.toml e acrescente o bloco abaixo."],
      code: {
        label: "~/.codex/config.toml",
        code: `experimental_use_rmcp_client = true

[mcp_servers.teia]
url = "${remote}"
startup_timeout_sec = 20
tool_timeout_sec = 120`,
      },
    },
    {
      id: "gemini",
      name: "Gemini CLI",
      mark: <MarkGemini size={38} />,
      kind: "remoto",
      summary: "O campo httpUrl aponta para servidores MCP hospedados.",
      steps: ["Edite ~/.gemini/settings.json (ou .gemini/settings.json do projeto)."],
      code: {
        label: "~/.gemini/settings.json",
        code: `{
  "mcpServers": {
    "teia": {
      "httpUrl": "${remote}",
      "timeout": 45000
    }
  }
}`,
      },
    },
    {
      id: "editores",
      name: "Cursor, VS Code, Windsurf, Cline e Copilot",
      mark: <MarkGroup size={38} marks={[MarkCursor, MarkVsCode, MarkWindsurf, MarkCline, MarkCopilot]} />,
      kind: "remoto",
      summary: "Todos leem o mesmo formato de mcp.json.",
      steps: [
        "Cursor: ~/.cursor/mcp.json (global) ou .cursor/mcp.json (projeto).",
        "VS Code: .vscode/mcp.json. Cline e Continue: painel de MCP do próprio plugin.",
      ],
      code: {
        label: "mcp.json",
        code: `{
  "mcpServers": {
    "teia": {
      "type": "http",
      "url": "${remote}"
    }
  }
}`,
      },
    },
    {
      id: "cherry",
      name: "DeepSeek, Qwen, Grok, Mistral e locais",
      mark: <MarkGroup size={38} marks={[MarkDeepSeek, MarkQwen, MarkGrok, MarkMistral, MarkOllama]} />,
      kind: "remoto",
      summary: "Os apps oficiais desses modelos ainda não instalam MCP; um cliente que suporte resolve.",
      steps: [
        "Use um cliente com MCP e escolha o modelo que quiser: Cherry Studio, Open WebUI, LibreChat, Cline.",
        "No Cherry Studio: Configurações → Servidores MCP → Adicionar, tipo Streamable HTTP.",
        `Endereço: ${remote}`,
      ],
      note: "Vale para DeepSeek, Qwen, Grok, Mistral, Llama e qualquer modelo que o cliente ofereça.",
    },
    {
      id: "ponte",
      name: "Qualquer cliente só com stdio",
      mark: <MarkTerminal size={38} />,
      kind: "ponte",
      summary: "Uma ponte de um arquivo, sem instalar nada, para clientes que não falam MCP remoto.",
      steps: [
        `Baixe a ponte: ${bridge}`,
        "Salve em qualquer pasta. Ela não tem dependência: roda com o Node que você já tem.",
        "Aponte o cliente para ela com o bloco abaixo.",
      ],
      code: {
        label: "claude_desktop_config.json, mcp.json ou equivalente",
        code: `{
  "mcpServers": {
    "teia": {
      "command": "node",
      "args": ["/caminho/para/teia-mcp.mjs"]
    }
  }
}`,
      },
    },
    {
      id: "api",
      name: "Sem MCP? Use a API",
      mark: <MarkApi size={38} />,
      kind: "api",
      summary: "As mesmas consultas em HTTP puro, para automação e scripts.",
      steps: ["Nenhuma chave, nenhum cadastro. Só respeite o limite de 60 requisições por minuto."],
      code: {
        label: "Terminal",
        code: `# ficha completa de um CNPJ
curl "${origin}/api/company?cnpj=33000167000101"

# busca por nome (empresas e sócios)
curl -X POST "${origin}/api/search" \\
  -H 'content-type: application/json' \\
  -d '{"type":"auto","q":"petrobras"}'

# rede societária
curl -X POST "${origin}/api/network" \\
  -H 'content-type: application/json' \\
  -d '{"cnpj":"33000167000101","depth":2}'`,
      },
    },
  ];
}

const TOOLS = [
  { name: "buscar_empresa", what: "Procura empresas e pessoas por nome, nome fantasia ou CNPJ." },
  { name: "ficha_cnpj", what: "Ficha cadastral completa: situação, endereço, CNAEs, capital, sócios e filiais." },
  { name: "socios_cnpj", what: "Só o quadro societário, com qualificação e data de entrada." },
  { name: "perfil_pessoa", what: "Empresas de uma pessoa e com quem ela costuma se associar." },
  { name: "rede_societaria", what: "Grafo de vínculos a partir de um CNPJ, camada por camada." },
];

const LIMITS = [
  {
    icon: <IconReload size={17} />,
    title: "O limite é folgado",
    text: "São 60 consultas por minuto por origem, com pico de 90. Quem usa pelo site tem o triplo disso, "
      + "porque escritório inteiro atrás de um IP só é normal. Estourou, vem um 429 dizendo quantos segundos esperar.",
  },
  {
    icon: <IconBraces size={17} />,
    title: "Precisa de mais ritmo?",
    text: `Uma chave de API dos planos pagos leva o limite para ${getPlan("pro").limites.apiPorMinuto} (Pro) ou `
      + `${getPlan("empresa").limites.apiPorMinuto} (Empresa) consultas por minuto, contadas pela chave. Mande `
      + "Authorization: Bearer teia_... no cliente, ou TEIA_CHAVE na ponte stdio. Sem chave, tudo segue aberto.",
  },
  {
    icon: <IconWeb size={17} />,
    title: "A teia pesa mais",
    text: "Montar o grafo consulta várias fontes de uma vez, então cada chamada vale por quatro. "
      + "Comece na profundidade 2 e só suba se a resposta pedir.",
  },
  {
    icon: <IconBuilding size={17} />,
    title: "É tudo dado público",
    text: "Sai dos dados abertos da Receita Federal, os mesmos que qualquer pessoa baixa do site dela. "
      + "De pessoa física só existe o miolo do CPF, no formato ***123456**.",
  },
  {
    icon: <IconAlert size={17} />,
    title: "Homônimo é armadilha",
    text: "Busca por nome junta gente diferente com o mesmo nome. Antes de afirmar qualquer coisa, "
      + "confira o conjunto: empresas, cidades e datas de entrada precisam fazer sentido juntos.",
  },
];

const PROMPTS = [
  "Quem são os sócios do CNPJ 33.000.167/0001-01?",
  "Monte a rede societária da Nubank até a segunda camada e me diga quem se repete.",
  "Quais empresas essa pessoa tem no quadro societário? Ela é sócia de alguma empresa baixada?",
  "Compare a situação cadastral e o capital social dessas cinco empresas.",
  "Esse CNPJ e esse outro têm algum sócio em comum?",
];

const COMPATIBLE = [
  { mark: <MarkClaude size={32} />, name: "Claude" },
  { mark: <MarkOpenAI size={32} />, name: "ChatGPT" },
  { mark: <MarkGemini size={32} />, name: "Gemini" },
  { mark: <MarkGrok size={32} />, name: "Grok" },
  { mark: <MarkDeepSeek size={32} />, name: "DeepSeek" },
  { mark: <MarkQwen size={32} />, name: "Qwen" },
  { mark: <MarkMistral size={32} />, name: "Mistral" },
  { mark: <MarkPerplexity size={32} />, name: "Perplexity" },
  { mark: <MarkCursor size={32} />, name: "Cursor" },
  { mark: <MarkVsCode size={32} />, name: "VS Code" },
  { mark: <MarkWindsurf size={32} />, name: "Windsurf" },
  { mark: <MarkCline size={32} />, name: "Cline" },
  { mark: <MarkCopilot size={32} />, name: "Copilot" },
  { mark: <MarkOllama size={32} />, name: "Ollama" },
];

export function ConnectPage() {
  const origin = useMemo(siteOrigin, []);
  // A ponte sai do próprio site: assim ela acompanha a versão no ar e não
  // depende da visibilidade do repositório.
  const bridgeUrl = `${origin}/teia-mcp.mjs`;
  const clients = useMemo(() => buildClients(origin, bridgeUrl), [origin, bridgeUrl]);
  const [open, setOpen] = useState<string>("claude");

  return (
    <main className="page container connect-page">
      <header className="connect-hero">
        <span className="badge accent">MCP gratuito</span>
        <h1>Conecte a sua IA ao Teia</h1>
        <p className="connect-lead">
          O Teia publica um servidor MCP aberto. Ligue o seu assistente a ele e pergunte sobre qualquer
          CNPJ em português: ficha cadastral, quadro societário, participações de uma pessoa e a rede de
          vínculos entre elas. Sem cadastro, sem chave e sem custo.
        </p>
        <CopyBlock label="Endereço do servidor MCP" code={`${origin}/mcp`} />
        <div className="compat-strip">
          {COMPATIBLE.map((item) => (
            <span className="compat-item" key={item.name}>
              {item.mark}
              <span>{item.name}</span>
            </span>
          ))}
        </div>
      </header>

      <section className="connect-section">
        <h2>Escolha o seu cliente</h2>
        <p className="section-lead">
          Clientes modernos aceitam a URL direto. Os que só falam stdio usam a ponte de um arquivo. Quem
          não tem MCP nenhum pode chamar a API em HTTP puro.
        </p>
        <div className="client-list">
          {clients.map((client) => {
            const expanded = open === client.id;
            return (
              <article className={`client-card${expanded ? " open" : ""}`} key={client.id}>
                <button className="client-head" onClick={() => setOpen(expanded ? "" : client.id)} aria-expanded={expanded}>
                  {client.mark}
                  <span className="client-title">
                    <span className="client-name">{client.name}</span>
                    <span className="client-summary">{client.summary}</span>
                  </span>
                  <span className={`badge ${client.kind === "remoto" ? "accent" : "plain"}`}>
                    {client.kind === "remoto" ? "URL direta" : client.kind === "ponte" ? "Ponte local" : "HTTP puro"}
                  </span>
                  <span className="client-chevron" aria-hidden>{expanded ? "−" : "+"}</span>
                </button>
                {expanded && (
                  <div className="client-body">
                    <ol className="client-steps">
                      {client.steps.map((step) => <li key={step}>{step}</li>)}
                    </ol>
                    {client.code && <CopyBlock label={client.code.label} code={client.code.code} />}
                    {client.note && <p className="client-note">{client.note}</p>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="connect-section">
        <h2>O que a IA passa a saber fazer</h2>
        <div className="tool-grid">
          {TOOLS.map((tool) => (
            <div className="tool-card" key={tool.name}>
              <span className="tool-name mono"><IconBraces size={14} /> {tool.name}</span>
              <span className="tool-what">{tool.what}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="connect-section">
        <h2>Perguntas para testar</h2>
        <ul className="prompt-list">
          {PROMPTS.map((prompt) => (
            <li key={prompt}><IconSearch size={14} /> <span>{prompt}</span></li>
          ))}
        </ul>
      </section>

      <section className="connect-section">
        <h2>Limites e bom senso</h2>
        <div className="limit-grid">
          {LIMITS.map((limit) => (
            <div className="limit-card" key={limit.title}>
              <span className="limit-icon" aria-hidden>{limit.icon}</span>
              <strong>{limit.title}</strong>
              <span>{limit.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="connect-cta">
        <span className="connect-cta-mark" aria-hidden><IconTeiaMark size={30} /></span>
        <div>
          <h2>Prefere ver com os olhos?</h2>
          <p>A mesma consulta, no site, com o grafo para arrastar e expandir.</p>
        </div>
        <a className="btn btn-primary" href="/"><IconWeb size={16} /> Abrir a busca</a>
      </section>
    </main>
  );
}
